import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '../App';

// Get the early-captured prompt from index.js or use local one
const getInstallPrompt = () => window.pwaInstallPrompt || null;

// Hilfsfunktion: ist der User mit dem Onboarding fertig?
// Banner/Modals sollen beim allerersten Login während Tour nicht aufploppen.
async function isOnboardingDone() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const me = await axios.get(`${API}/auth/me`);
    return !!me.data?.tour_completed;
  } catch { return false; }
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(getInstallPrompt);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  // Hilfsfunktion: darf Banner gezeigt werden? (gleiche 7-Tage-Logik wie unten)
  const canShowBanner = () => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) return false;
    const dismissedTime = localStorage.getItem('pwaInstallDismissed');
    if (dismissedTime && Date.now() - parseInt(dismissedTime) < 7 * 24 * 60 * 60 * 1000) return false;
    return !!(window.pwaInstallPrompt);
  };

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Tour-Start → Banner verstecken; Tour-Ende → wieder anzeigen (falls noch gültig)
    const hideBanner = () => setShowBanner(false);
    const restoreBanner = () => {
      if (canShowBanner()) isOnboardingDone().then(done => { if (done) setShowBanner(true); });
    };
    window.addEventListener('bt-tour-start', hideBanner);
    window.addEventListener('bt-tour-end', restoreBanner);

    // Zeige Banner wenn nicht dismissed und nicht Tour aktiv
    const dismissedTime = localStorage.getItem('pwaInstallDismissed');
    const recentlyDismissed = dismissedTime && Date.now() - parseInt(dismissedTime) < 7 * 24 * 60 * 60 * 1000;

    if (!recentlyDismissed) {
      // If we already have the global prompt (from index.js), show banner — aber NICHT während Onboarding
      if (window.pwaInstallPrompt && !window.burningTeamTourActive) {
        setDeferredPrompt(window.pwaInstallPrompt);
        isOnboardingDone().then(done => { if (done) setShowBanner(true); });
      }

      // Listen for the beforeinstallprompt event (in case it fires later)
      const handleBeforeInstallPrompt = (e) => {
        e.preventDefault();
        window.pwaInstallPrompt = e;
        setDeferredPrompt(e);
        if (!window.burningTeamTourActive) {
          isOnboardingDone().then(done => { if (done) setShowBanner(true); });
        }
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      // Listen for successful installation
      const handleAppInstalled = () => {
        setIsInstalled(true);
        setShowBanner(false);
        setDeferredPrompt(null);
        window.pwaInstallPrompt = null;
        localStorage.removeItem('pwaInstallDismissed');
      };
      window.addEventListener('appinstalled', handleAppInstalled);

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
        window.removeEventListener('bt-tour-start', hideBanner);
        window.removeEventListener('bt-tour-end', restoreBanner);
      };
    }

    return () => {
      window.removeEventListener('bt-tour-start', hideBanner);
      window.removeEventListener('bt-tour-end', restoreBanner);
    };
  }, []); // eslint-disable-line

  const handleInstall = async () => {
    const prompt = deferredPrompt || window.pwaInstallPrompt;
    if (!prompt) return;

    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('PWA installation accepted');
    } else {
      console.log('PWA installation dismissed');
    }
    
    setDeferredPrompt(null);
    window.pwaInstallPrompt = null;
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwaInstallDismissed', Date.now().toString());
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-xl shadow-2xl border border-emerald-200 p-4 z-50 animate-slide-up">
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 transition-colors"
        aria-label="Schließen"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
          <Smartphone className="w-6 h-6 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-800 text-lg">App installieren</h3>
          <p className="text-slate-600 text-sm mt-1">
            Installiere BurningTeam für schnellen Zugriff – direkt von deinem Homescreen!
          </p>
          
          <div className="flex gap-2 mt-3">
            <Button
              onClick={handleInstall}
              className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white text-sm px-4 py-2 h-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              Installieren
            </Button>
            <Button
              onClick={handleDismiss}
              variant="ghost"
              className="text-slate-500 hover:text-slate-700 text-sm px-3 py-2 h-auto"
            >
              Später
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export hook for use in Settings page
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(getInstallPrompt);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstallable, setIsInstallable] = useState(!!window.pwaInstallPrompt);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Check if we already have the prompt (from index.js)
    if (window.pwaInstallPrompt) {
      setDeferredPrompt(window.pwaInstallPrompt);
      setIsInstallable(true);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      window.pwaInstallPrompt = e;
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      window.pwaInstallPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    const prompt = deferredPrompt || window.pwaInstallPrompt;
    if (!prompt) return false;

    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    setDeferredPrompt(null);
    window.pwaInstallPrompt = null;
    setIsInstallable(false);
    
    return outcome === 'accepted';
  };

  return { isInstalled, isInstallable, promptInstall };
}
